import {
  contentChildren,
  Directive,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  InputSignal,
  Output,
  Signal, OnDestroy
} from '@angular/core';
import { ObserveDirective } from './observe.directive';
import { INTERSECTION_OBSERVER_EMIT_STRATEGIES, INTERSECTION_OBSERVER_STRATEGIES, IntersectionObserverEmitStrategy, IntersectionObserverOptions, IntersectionObserverStrategy } from './types/intersection-observer.type';

function getIntersectionObserverOptionsFromProperties (threshold?: number | number[], rootMargin?: string): IntersectionObserverOptions | undefined {
  return {
    ...threshold ? { threshold } : {},
    ...rootMargin ? { rootMargin } : {}
  };
}

/**
 * Directive that marks an element as the root of an `IntersectionObserver`.
 *
 * All descendant elements that are decorated with {@link ObserveDirective | appObserve}
 * are observed according to the selected strategy (`observationStrategy`).
 * Intersection changes are emitted through the `intersecting` output.
 *
 * Example:
 * ```html
 * <div
 *   appIntersectionObserver
 *   observationStrategy="last"
 *   [options]="{ threshold: 0.5 }"
 *   (intersecting)="onIntersect($event)"
 * >
 *   <div appObserve></div>
 *   <div appObserve></div>
 *   <div appObserve></div>
 * </div>
 * ```
 */
@Directive({
  selector: '[appIntersectionObserver]',
  standalone: true
})
export class IntersectionObserverDirective implements OnDestroy {
  /**
   * Host element that acts as the `root` for the underlying `IntersectionObserver`.
   * The intersection of observed elements is computed relative to this element.
   */
  private readonly _root: ElementRef<HTMLElement> | null
    = inject<ElementRef<HTMLElement>>(ElementRef<HTMLElement>, { optional: true });

  public readonly root: InputSignal<ElementRef<HTMLElement> | null> = input(this._root);

  private _observations: Signal<readonly ObserveDirective[] | undefined>
    = contentChildren(ObserveDirective, { descendants: true });

  private _intersectionObservers: IntersectionObserver[] = [];

  /**
   * Emits whenever the underlying `IntersectionObserver` produces new
   * `IntersectionObserverEntry` records and the `observationStrategy` matches the case.
   */
  @Output()
  public readonly intersecting: EventEmitter<IntersectionObserverEntry>
    = new EventEmitter<IntersectionObserverEntry>();

  /**
   * Strategy that controls which observed elements are actually registered
   * with the `IntersectionObserver`.
   *
   * - `'all'`   – observe all marked elements
   * - `'first'` – observe only the first marked element
   * - `'last'`  – observe only the last marked element
   *
   * Defaults to `'all'`.
   */
  public readonly observationStrategy: InputSignal<IntersectionObserverStrategy>
    = input<IntersectionObserverStrategy>(INTERSECTION_OBSERVER_STRATEGIES.all);


  public readonly emitStrategy: InputSignal<IntersectionObserverEmitStrategy>
    = input<IntersectionObserverEmitStrategy>(INTERSECTION_OBSERVER_EMIT_STRATEGIES.enter);

  public readonly threshold: InputSignal<number | number[] | undefined> = input();

  public readonly rootMargin: InputSignal<string | undefined> = input();


  constructor () {
    effect(() => {
      const root: ElementRef<HTMLElement> | null = this.root();

      if (!root) return;

      const strategy: IntersectionObserverStrategy = this.observationStrategy();
      const options: IntersectionObserverOptions | undefined = getIntersectionObserverOptionsFromProperties(this.threshold(), this.rootMargin());
      const intersections: readonly Element[] | undefined
        = this._observations()
          ?.map((observation: ObserveDirective) => observation.elementRef.nativeElement);

      if (!intersections || intersections.length === 0) return;

      this.cleanUpObserver();

      switch (strategy) {
        case INTERSECTION_OBSERVER_STRATEGIES.all: {
          for (const intersection of intersections) {
            const observer: IntersectionObserver = this.getObserver(root, options);

            this._intersectionObservers.push(observer);
            observer.observe(intersection);
          }

          break;
        }

        case INTERSECTION_OBSERVER_STRATEGIES.first: {
          const observer: IntersectionObserver = this.getObserver(root, options);

          this._intersectionObservers.push(observer);
          // eslint-disable-next-line @tseslint/no-non-null-assertion
          observer.observe(intersections[ 0 ]!);

          break;
        }

        case INTERSECTION_OBSERVER_STRATEGIES.last: {
          const observer: IntersectionObserver = this.getObserver(root, options);

          this._intersectionObservers.push(observer);
          // eslint-disable-next-line @tseslint/no-non-null-assertion
          observer.observe(intersections.at(-1)!);

          break;
        }
      }
    }, { allowSignalWrites: true });
  }


  public ngOnDestroy (): void {
    this.cleanUpObserver();
  }

  private cleanUpObserver (): void {
    for (const observer of this._intersectionObservers)
      observer.disconnect();

    this._intersectionObservers = [];
  }

  private getObserver (root: ElementRef<HTMLElement>, options?: IntersectionObserverOptions): IntersectionObserver {
    return new IntersectionObserver(this._observe, { ...options, root: root.nativeElement });
  }

  private _observe: IntersectionObserverCallback = (entries: IntersectionObserverEntry[]) => {
    if (entries.length === 0) return;

    // eslint-disable-next-line @tseslint/no-non-null-assertion
    const entry: IntersectionObserverEntry = entries.at(0)!;

    switch (this.emitStrategy()) {
      case INTERSECTION_OBSERVER_EMIT_STRATEGIES.all: {
        this.intersecting.emit(entry);

        break;
      }

      case INTERSECTION_OBSERVER_EMIT_STRATEGIES.enter: {
        if (entry.isIntersecting)
          this.intersecting.emit(entries.at(0));

        break;
      }

      case INTERSECTION_OBSERVER_EMIT_STRATEGIES.leave: {
        if (!entry.isIntersecting)
          this.intersecting.emit(entries.at(0));
      }
    }
  };
}
